import { action, computed, observable, reaction, makeObservable } from 'mobx';
import {X8BlockType, X9Content, X9ContentType} from '../../../domain';
import {Disposers, toastrHelper} from '../../../lib/helpers';
import {AddBlockStore} from './block-parts/AddBlockStore';
import {DeleteBlockStore} from './block-parts/DeleteBlockStore';
import {ReorderBlocksStore} from './block-parts/ReorderBlocksStore';
import {AddContentStore} from './content-parts/AddContentStore';
import {DeleteContentStore} from './content-parts/DeleteContentStore';
import {EditContentStore} from './content-parts/EditContentStore';
import {ReorderContentsStore} from './content-parts/ReorderContentsStore';
import {X7PushableBlockLoader} from './X7PushableBlockLoader';

type Mode = 'view' | 'add-block' | 'reorder-blocks' | 'delete-block' |
  'add-content' | 'edit-content' | 'reorder-contents' | 'delete-content';

export class X7PushableBlockStore {
  readonly loader: X7PushableBlockLoader;

  @observable
  mode: Mode;

  @observable.ref
  addBlock: AddBlockStore | null;
  @observable.ref
  reorderBlocks: ReorderBlocksStore | null;
  @observable.ref
  deleteBlock: DeleteBlockStore | null;

  @observable.ref
  addContent: AddContentStore | null;
  @observable.ref
  editContent: EditContentStore | null;
  @observable.ref
  reorderContents: ReorderContentsStore | null;
  @observable.ref
  deleteContent: DeleteContentStore | null;

  readonly disposers = new Disposers();

  constructor(public readonly pushableBlockId: number) {
    makeObservable(this);
    this.mode = 'view';

    this.loader = new X7PushableBlockLoader(pushableBlockId);
    this.disposers.add(this.loader);
    this.addBlock = this.deleteBlock = this.reorderBlocks = null;
    this.addContent = this.editContent = this.reorderContents = this.deleteContent = null;
    this.setup();
  }

  @action
  private toViewMode() {
    this.mode = 'view';
    this.addBlock = this.deleteBlock = this.reorderBlocks = null;
    this.addContent = this.editContent = this.reorderContents = this.deleteContent = null;
  }

  @computed
  private get isSaving(): boolean {
    return this.addBlock?.save?.isPending === true ||
      this.reorderBlocks?.save?.isPending === true ||
      this.deleteBlock?.save?.isPending === true ||
      this.addContent?.save?.isPending === true ||
      this.editContent?.save?.isPending === true ||
      this.reorderContents?.save?.isPending === true ||
      this.deleteContent?.save?.isPending === true;
  }

  /****************************************************************************/

  @action
  beginAddBlock = (blockType: X8BlockType) => {
    this.mode = 'add-block';
    this.addBlock = new AddBlockStore(this.pushableBlockId, blockType);
    this.completeAddBlock();
  };

  @action
  completeAddBlock = () => {
    this.addBlock?.complete();
  };

  /****************************************************************************/

  @action
  beginReorderBlocks = () => {
    this.mode = 'reorder-blocks';
    const blockIds = this.loader.blocks.map(x => x.Id);
    this.reorderBlocks = new ReorderBlocksStore(this.pushableBlockId, blockIds);
  };

  @action
  reorderBlockUp = (index: number) => {
    this.reorderBlocks?.swap(index, index - 1);
  };

  @action
  reorderBlockDown = (index: number) => {
    this.reorderBlocks?.swap(index, index + 1);
  };

  @action
  completeReorderBlocks = () => {
    this.reorderBlocks?.complete();
  };

  /****************************************************************************/

  @action
  beginDeleteBlock = (blockId: number) => {
    this.mode = 'delete-block';
    this.deleteBlock = new DeleteBlockStore(blockId);

    this.completeDeleteBlock();
  };

  @action
  completeDeleteBlock = () => {
    this.deleteBlock?.complete();
  };
    /****************************************************************************/
  @action
  beginBalBlock = (blockId: number, blockBal: number, blockType: X8BlockType) => {    
      if(this.addBlock == null)
        this.addBlock = new AddBlockStore(this.pushableBlockId, blockType);
       this.addBlock?.completeBal(blockId, blockBal);
  };  
  /****************************************************************************/

  @action
  beginAddContent = (blockId: number, contentType: X9ContentType) => {
    this.mode = 'add-content';
    this.addContent = new AddContentStore(this, blockId, contentType);
  };

  /****************************************************************************/

  @action
  beginEditContent = (content: X9Content) => {
    this.mode = 'edit-content';
    this.editContent = new EditContentStore(this, content);
  };

  /****************************************************************************/

  @action
  beginReorderContents = (blockId: number) => {
    const block = this.loader.blocksById[blockId];
    const contentIds = block.Items!.map(x => x.Id);
    this.mode = 'reorder-contents';
    this.reorderContents = new ReorderContentsStore(blockId, contentIds);
  };

  @action
  reorderContentUp = (index: number) => {
    this.reorderContents?.swap(index, index - 1);
  };

  @action
  reorderContentDown = (index: number) => {
    this.reorderContents?.swap(index, index + 1);
  };

  @action
  completeReorderContents = () => {
    this.reorderContents?.complete();
  };

  /****************************************************************************/
  @action
  beginDeleteContent = (blockId: number, contentId: number) => {
    this.mode = 'delete-content';
    this.deleteContent = new DeleteContentStore(blockId, contentId);
    this.completeDeleteContent();
  };

  @action
  completeDeleteContent = () => {
    this.deleteContent?.complete();
  };

  /****************************************************************************/

  @action
  cancel = () => {
    if (this.isSaving)
      return;
    this.toViewMode();
  };

  /****************************************************************************/

  setup() {
    this.disposers.add(reaction(
      () => [
        this.addBlock?.save?.state,
        this.reorderBlocks?.save?.state,
        this.deleteBlock?.save?.state,
        this.addContent?.save?.state,
        this.editContent?.save?.state,
        this.reorderContents?.save?.state,
        this.deleteContent?.save?.state],
      this.reloadOnSuccessResponse
    ));
  }

  @action
  reloadOnSuccessResponse = () => {
    for (let state of [
      this.addBlock?.save,
      this.reorderBlocks?.save,
      this.deleteBlock?.save,
      this.addContent?.save,
      this.editContent?.save,
      this.reorderContents?.save,
      this.deleteContent?.save])
      if (state?.isResolved === true) {
        toastrHelper.success(state.value);
        this.toViewMode();
        this.loader.load();
      }
  };

  dispose() {
    this.disposers.dispose();
  }
}
